import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, BadgeCheck } from "lucide-react";
import { PageHeader, Loader, Badge } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { statusColor } from "@/lib/format";

const FileField = ({ label, name, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <label className="mt-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-white/15 bg-white/5 cursor-pointer hover:border-orange-500/50 transition-colors">
      <Upload className="w-5 h-5 text-orange-400" />
      <span className="text-sm text-gray-400 truncate" data-testid={`${testid}-name`}>{onChange.file?.name || "Choose from gallery / camera / device"}</span>
      <input type="file" accept="image/*,.pdf" className="hidden" data-testid={testid} onChange={(e) => onChange(e.target.files[0])} />
    </label>
  </div>
);

export default function Kyc() {
  const { t } = useI18n();
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState({ full_name: "", id_number: "", address: "" });
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/kyc").then(({ data }) => setKyc(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!kyc) return <Loader />;

  const submit = async (e) => {
    e.preventDefault();
    if (!files.id_front || !files.id_back) { toast.error("ID Front and ID Back are required"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("full_name", form.full_name); fd.append("id_number", form.id_number);
    fd.append("id_front", files.id_front); fd.append("id_back", files.id_back);
    if (files.profile_photo) fd.append("profile_photo", files.profile_photo);
    try {
      await api.post("/kyc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("KYC submitted for review!");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitted = kyc.status && kyc.status !== "not_submitted";
  const setFile = (k) => (f) => setFiles((p) => ({ ...p, [k]: f }));

  return (
    <div>
      <PageHeader title={t("kyc")} subtitle="Verify your identity to unlock withdrawals and higher limits."
        action={submitted && <Badge className={statusColor(kyc.status)}>{t("kyc_status")}: {kyc.status.replace("_", " ")}</Badge>} />

      {submitted && kyc.status !== "rejected" && kyc.status !== "additional_required" ? (
        <div className="ec-card p-10 text-center">
          <BadgeCheck className="w-14 h-14 text-orange-400 mx-auto" />
          <h3 className="font-head text-xl font-semibold text-white mt-4">Your KYC is {kyc.status.replace("_", " ")}</h3>
          <p className="text-gray-400 mt-2">We'll notify you once the review is complete.</p>
          {kyc.reason && <p className="text-yellow-400 mt-3 text-sm">{kyc.reason}</p>}
        </div>
      ) : (
        <form onSubmit={submit} className="ec-card p-6 sm:p-8 space-y-5 max-w-2xl">
          {kyc.status === "rejected" && kyc.reason && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-xl p-3">Rejected: {kyc.reason}. Please resubmit.</p>}
          {kyc.status === "additional_required" && <p className="text-yellow-400 text-sm bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">More documents needed: {kyc.reason}</p>}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>{t("full_name")}</Label><Input data-testid="kyc-fullname" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="mt-1" /></div>
            <div><Label>{t("id_number")}</Label><Input data-testid="kyc-idnumber" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} required className="mt-1" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FileField label={t("id_front")} onChange={Object.assign(setFile("id_front"), { file: files.id_front })} testid="kyc-idfront" />
            <FileField label={t("id_back")} onChange={Object.assign(setFile("id_back"), { file: files.id_back })} testid="kyc-idback" />
            <FileField label={t("profile_photo")} onChange={Object.assign(setFile("profile_photo"), { file: files.profile_photo })} testid="kyc-photo" />
          </div>
          <Button type="submit" disabled={busy} data-testid="kyc-upload-submit" className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold px-8">{busy ? "..." : t("submit_kyc")}</Button>
        </form>
      )}
    </div>
  );
}
